'use client';

import { useState, useRef, useCallback } from 'react';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';

interface PhotoWithCaption {
  file: File;
  preview: string;
  caption: string;
}

interface AddPhotoModalProps extends BaseModalProps {
  onPhotosAdded?: (photos: File[]) => void;
  onPhotoWithCaptionAdded?: (photo: { file: File; caption: string }) => void;
  maxPhotos?: number;
  maxSizeMB?: number;
  allowedFormats?: string[];
  title?: string;
  /** Enable single photo mode with caption input (for portfolio) */
  singlePhotoMode?: boolean;
}

/**
 * Add Photo Modal - allows users to upload photos
 * Supports two modes:
 * - Multiple photos (default): Upload multiple photos at once
 * - Single photo with caption: Upload one photo with a text caption (for portfolio)
 *
 * Usage:
 * - Multiple: const { open } = useModal('add-photo'); open({ onPhotosAdded: (photos) => {...} });
 * - Single with caption: open({ singlePhotoMode: true, onPhotoWithCaptionAdded: (photo) => {...} });
 */
export function AddPhotoModal({
  isOpen,
  onClose,
  onPhotosAdded,
  onPhotoWithCaptionAdded,
  maxPhotos = 10,
  maxSizeMB = 10,
  allowedFormats = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'],
  title = 'Add Photos',
  singlePhotoMode = false,
}: AddPhotoModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [caption, setCaption] = useState('');
  const [singlePhoto, setSinglePhoto] = useState<PhotoWithCaption | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!allowedFormats.includes(file.type)) {
      return `${file.name}: Invalid format. Allowed: JPEG, PNG, WebP`;
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      return `${file.name}: File too large (max ${maxSizeMB}MB)`;
    }

    return null;
  };

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const newErrors: string[] = [];

      if (singlePhotoMode) {
        // Single photo mode - only take first file
        const file = files[0];
        if (file) {
          const error = validateFile(file);
          if (error) {
            newErrors.push(error);
            setErrors(newErrors);
          } else {
            const reader = new FileReader();
            reader.onloadend = () => {
              setSinglePhoto({
                file,
                preview: reader.result as string,
                caption: '',
              });
            };
            reader.readAsDataURL(file);
            setErrors([]);
          }
        }
      } else {
        // Multiple photos mode
        const validFiles: File[] = [];
        const newPreviews: string[] = [];

        // Check if adding these files would exceed max photos
        if (selectedFiles.length + files.length > maxPhotos) {
          newErrors.push(`Maximum ${maxPhotos} photos allowed`);
          setErrors(newErrors);
          return;
        }

        Array.from(files).forEach((file) => {
          const error = validateFile(file);
          if (error) {
            newErrors.push(error);
          } else {
            validFiles.push(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
              newPreviews.push(reader.result as string);
              if (newPreviews.length === validFiles.length) {
                setPreviews([...previews, ...newPreviews]);
              }
            };
            reader.readAsDataURL(file);
          }
        });

        setSelectedFiles([...selectedFiles, ...validFiles]);
        setErrors(newErrors);
      }
    },
    [singlePhotoMode, selectedFiles, previews, maxPhotos, allowedFormats, maxSizeMB]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemovePhoto = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
    setErrors([]);
  };

  const handleRemoveSinglePhoto = () => {
    setSinglePhoto(null);
    setCaption('');
    setErrors([]);
  };

  const handleSubmit = () => {
    if (singlePhotoMode) {
      if (!singlePhoto) return;
      onPhotoWithCaptionAdded?.({
        file: singlePhoto.file,
        caption: caption.trim(),
      });
    } else {
      if (selectedFiles.length === 0) return;
      onPhotosAdded?.(selectedFiles);
    }

    handleCancel();
  };

  const handleCancel = () => {
    onClose();
    // Reset state
    setSelectedFiles([]);
    setPreviews([]);
    setErrors([]);
    setCaption('');
    setSinglePhoto(null);
  };

  // Render single photo mode with caption (portfolio style)
  if (singlePhotoMode) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        size="md"
        animation="slide-up"
      >
        <div className="p-5 relative bg-BG-Background-1 rounded-[30px] inline-flex flex-col justify-center items-center gap-5 overflow-hidden">
          {/* Header */}
          <div className="w-full inline-flex justify-between items-center">
            <div className="text-Text-Text-Primary text-2xl font-semibold font-['Roboto'] leading-8">
              {title || 'Add photo'}
            </div>
            <button
              onClick={handleCancel}
              className="w-12 h-12 relative flex items-center justify-center hover:bg-background-secondary rounded-full transition-colors"
            >
              <svg
                className="w-6 h-6 text-Text-Text-Primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Image area */}
          <div className="self-stretch flex flex-col justify-start items-center gap-2.5">
            {singlePhoto ? (
              <div className="self-stretch inline-flex justify-start items-center relative group">
                <img
                  className="flex-1 h-72 object-cover rounded-md"
                  src={singlePhoto.preview}
                  alt="Preview"
                />
                {/* Remove button */}
                <button
                  type="button"
                  onClick={handleRemoveSinglePhoto}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  aria-label="Remove photo"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full h-72 rounded-md border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-Accents-Accent-1 bg-Accents-Accent-1/5'
                    : 'border-border-primary hover:border-Accents-Accent-2 bg-surface-sunken'
                }`}
              >
                <svg
                  className="w-12 h-12 text-text-secondary mb-4"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-text-secondary text-sm">
                  Click to upload or drag and drop
                </p>
                <p className="text-text-tertiary text-xs mt-1">
                  JPEG, PNG, WebP up to {maxSizeMB}MB
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedFormats.join(',')}
              onChange={handleInputChange}
              className="hidden"
            />
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
              {errors.map((error, index) => (
                <p key={index} className="text-xs text-red-600">
                  {error}
                </p>
              ))}
            </div>
          )}

          {/* Caption input and Publish button */}
          <div className="w-full inline-flex justify-end items-end gap-5">
            <div className="flex-1 h-12 rounded-[10px] inline-flex flex-col justify-center items-start gap-5">
              <div className="self-stretch flex-1 flex flex-col justify-center items-start gap-[5px]">
                <div className="self-stretch flex-1 p-3.5 bg-BG-Background-1 rounded-[20px] shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)] outline outline-1 outline-offset-[-1px] outline-Accents-Accent-2 inline-flex justify-between items-center">
                  <div className="flex-1 max-h-44 flex justify-start items-end gap-1 overflow-hidden">
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Add a caption..."
                      className="flex-1 bg-transparent border-none outline-none text-Text-Text-Primary text-base font-normal font-['Roboto'] leading-5 placeholder:text-Text-Text-Unfocused"
                      maxLength={200}
                    />
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!singlePhoto}
              className="w-24 h-12 px-6 py-3.5 bg-Accents-Accent-1 rounded-full flex justify-center items-center gap-2.5 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors hover:opacity-90"
            >
              <span className="text-Text-Text-Inverse text-xl font-medium font-['Roboto'] leading-6">
                Publish
              </span>
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Render multiple photos mode (original behavior)
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="lg"
      animation="slide-up"
      title={title}
      description={`Upload up to ${maxPhotos} photos (max ${maxSizeMB}MB each)`}
    >
      <div className="space-y-4">
        {/* Upload Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedFormats.join(',')}
            multiple
            onChange={handleInputChange}
            className="hidden"
          />

          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <p className="mt-4 text-sm text-gray-600">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Click to upload
            </button>{' '}
            or drag and drop
          </p>
          <p className="mt-1 text-xs text-gray-500">
            JPEG, PNG, WebP up to {maxSizeMB}MB
          </p>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="text-xs text-red-600">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Grid */}
        {previews.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Selected Photos ({selectedFiles.length}/{maxPhotos})
            </p>
            <div className="grid grid-cols-3 gap-3 max-h-80 overflow-y-auto">
              {previews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    aria-label="Remove photo"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                  {/* File info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs truncate">
                      {selectedFiles[index]?.name}
                    </p>
                    <p className="text-xs">
                      {(selectedFiles[index]?.size / 1024).toFixed(0)} KB
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Add {selectedFiles.length} {selectedFiles.length === 1 ? 'Photo' : 'Photos'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
